import React, { useEffect, useMemo, useState } from 'react';
import { getCachedAccessibleStores } from '@/lib/runtimeMetaCache';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  getIntercompanyModes,
  upsertIntercompanyMode,
  getIntercompanyMonthlyRateGrid,
  upsertIntercompanyMonthlyRateGrid,
  generateIntercompanyStatement,
  getIntercompanyStatements,
  updateIntercompanyStatementStatus,
  getIntercompanyRemittances,
  upsertIntercompanyRemittances,
  deleteIntercompanyRemittance,
  uploadIntercompanyRemittanceAttachment,
  parseIntercompanyRemittancePdf,
  downloadIntercompanyRemittanceAttachment,
  getIntercompanyReconciliation,
  autoGenerateIntercompanyDraftsForReconciliation,
  downloadIntercompanyStatementInvoicePdf,
} from '@/lib/api';

const monthNow = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const resolveModeForMonth = (modes, store, month) => {
  const override = (modes || []).find((m) => m.store === store && m.month === month);
  return override?.mode || 'basic';
};


const monthRange = (startMonth, endMonth) => {
  const out = [];
  const [sy, sm] = String(startMonth || '').split('-').map(Number);
  const [ey, em] = String(endMonth || '').split('-').map(Number);
  if (!sy || !sm || !ey || !em) return out;
  let y = sy;
  let m = sm;
  while (y < ey || (y === ey && m <= em)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
};

const getNested = (obj, path) => path.split('.').reduce((acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined), obj);

const setNested = (obj, path, value) => {
  const next = { ...(obj || {}) };
  const keys = path.split('.');
  let cur = next;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const k = keys[i];
    cur[k] = { ...(cur[k] || {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return next;
};

const bulkFieldOptions = [
  { value: 'franchise_percent', label: 'Franchise %' },
  { value: 'fabric_percent_basic', label: 'Fabric % (Basic)' },
  { value: 'alteration_bulk_amount', label: 'Alterations (Monthly)' },
  { value: 'logistics_rates.default', label: 'Logistics (default)' },
  { value: 'muslin_rates.jacket', label: 'Muslin Jacket' },
  { value: 'muslin_rates.trouser', label: 'Muslin Trouser' },
  { value: 'muslin_rates.waistcoat', label: 'Muslin Waistcoat' },
  { value: 'muslin_rates.overcoat', label: 'Muslin Overcoat' },
  { value: 'muslin_rates.shirt', label: 'Muslin Shirt' },
  { value: 'baste_rates.jacket', label: 'Baste Jacket' },
  { value: 'baste_rates.trouser', label: 'Baste Trouser' },
  { value: 'baste_rates.waistcoat', label: 'Baste Waistcoat' },
  { value: 'baste_rates.overcoat', label: 'Baste Overcoat' },
  { value: 'construction_rates.jacket', label: 'Construction Jacket' },
  { value: 'construction_rates.trouser', label: 'Construction Trouser' },
  { value: 'construction_rates.waistcoat', label: 'Construction Waistcoat' },
  { value: 'construction_rates.overcoat', label: 'Construction Overcoat' },
  { value: 'construction_rates.shirt', label: 'Construction Shirt' },
  { value: 'construction_rates.shoe', label: 'Construction Shoe' },
  { value: 'construction_rates.tie', label: 'Construction Tie' },
  { value: 'construction_rates.pocket_square', label: 'Construction Pocket Square' },
];

const editableBulkColumns = [
  { key: 'franchise_percent', label: 'Franchise %' },
  { key: 'fabric_percent_basic', label: 'Fabric %' },
  { key: 'alteration_bulk_amount', label: 'Alterations' },
  { key: 'logistics_rates.default', label: 'Logistics' },
  { key: 'muslin_rates.jacket', label: 'Muslin Jkt' },
  { key: 'muslin_rates.trouser', label: 'Muslin Trs' },
  { key: 'muslin_rates.waistcoat', label: 'Muslin Wct' },
  { key: 'muslin_rates.overcoat', label: 'Muslin Ovt' },
  { key: 'muslin_rates.shirt', label: 'Muslin Shirt' },
  { key: 'baste_rates.jacket', label: 'Baste Jkt' },
  { key: 'baste_rates.trouser', label: 'Baste Trs' },
  { key: 'baste_rates.waistcoat', label: 'Baste Wct' },
  { key: 'baste_rates.overcoat', label: 'Baste Ovt' },
  { key: 'construction_rates.jacket', label: 'Const Jkt' },
  { key: 'construction_rates.trouser', label: 'Const Trs' },
  { key: 'construction_rates.waistcoat', label: 'Const Wct' },
  { key: 'construction_rates.overcoat', label: 'Const Ovt' },
  { key: 'construction_rates.shirt', label: 'Const Shirt' },
  { key: 'construction_rates.shoe', label: 'Const Shoe' },
  { key: 'construction_rates.tie', label: 'Const Tie' },
  { key: 'construction_rates.pocket_square', label: 'Const PS' },
];

const defaultRemittanceRow = (month = '') => ({
  entry_id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  month,
  remittance_date: month ? `${month}-01` : '',
  reference: '',
  amount_aud: 0,
  amount_sgd: 0,
  fee_aud: 0,
  fee_sgd: 0,
  source: 'manual',
  notes: '',
  attachment_url: '',
  attachment_name: '',
  attachment_r2_key: '',
  parse_confidence: 0,
  raw_line: '',
  is_deleted: false,
});

const toDateTimestamp = (value) => {
  if (!value) return Number.NaN;
  const direct = Date.parse(String(value));
  if (!Number.isNaN(direct)) return direct;

  const maybeMonth = String(value).match(/^(\d{4})-(\d{2})$/);
  if (maybeMonth) {
    const fallback = Date.parse(`${maybeMonth[1]}-${maybeMonth[2]}-01`);
    return Number.isNaN(fallback) ? Number.NaN : fallback;
  }
  return Number.NaN;
};

const parseStoreOptions = async () => {
  const data = await getCachedAccessibleStores();
  if (Array.isArray(data)) return data;
  return data?.stores || [];
};

export default function IntercompanyCostingPage() {
  const [stores, setStores] = useState([]);
  const [store, setStore] = useState('');
  const [month, setMonth] = useState(monthNow());
  const [mode, setMode] = useState('basic');
  const [modesCache, setModesCache] = useState([]);
  const [statements, setStatements] = useState([]);
  const [includeCold, setIncludeCold] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconciliation, setReconciliation] = useState(null);
  const [reconStartMonth, setReconStartMonth] = useState('2025-06');
  const [reconEndMonth, setReconEndMonth] = useState('2026-05');
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkSelection, setBulkSelection] = useState({});
  const [bulkField, setBulkField] = useState('franchise_percent');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkDirtyMonths, setBulkDirtyMonths] = useState({});
  const [copyFromMonth, setCopyFromMonth] = useState('');
  const [remittanceRows, setRemittanceRows] = useState([]);
  const [remittanceLoading, setRemittanceLoading] = useState(false);
  const [remittanceSaving, setRemittanceSaving] = useState(false);
  const [parsedRemittanceRows, setParsedRemittanceRows] = useState([]);
  const [parsingRemittancePdf, setParsingRemittancePdf] = useState(false);
  const [remittancePdfFile, setRemittancePdfFile] = useState(null);
  const [remittanceSortField, setRemittanceSortField] = useState('remittance_date');
  const [remittanceSortDirection, setRemittanceSortDirection] = useState('desc');

  const sortedRemittanceRows = useMemo(() => {
    const rows = [...(remittanceRows || [])];
    const dir = remittanceSortDirection === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const aVal = a?.[remittanceSortField];
      const bVal = b?.[remittanceSortField];

      if (remittanceSortField === 'remittance_date') {
        const aTs = toDateTimestamp(aVal);
        const bTs = toDateTimestamp(bVal);
        const aMissing = Number.isNaN(aTs);
        const bMissing = Number.isNaN(bTs);
        if (aMissing && !bMissing) return 1;
        if (!aMissing && bMissing) return -1;
        if (aMissing && bMissing) return 0;
        return (aTs - bTs) * dir;
      }

      if (remittanceSortField === 'month') {
        const aTs = toDateTimestamp(aVal);
        const bTs = toDateTimestamp(bVal);
        const aSafe = Number.isNaN(aTs) ? Number.NEGATIVE_INFINITY : aTs;
        const bSafe = Number.isNaN(bTs) ? Number.NEGATIVE_INFINITY : bTs;
        return (aSafe - bSafe) * dir;
      }

      if (remittanceSortField === 'amount_aud' || remittanceSortField === 'amount_sgd') {
        return (Number(aVal || 0) - Number(bVal || 0)) * dir;
      }
      return String(aVal || '').localeCompare(String(bVal || '')) * dir;
    });
    return rows;
  }, [remittanceRows, remittanceSortField, remittanceSortDirection]);

  useEffect(() => {
    const load = async () => {
      try {
        const [storeList, modesRes, statementsRes] = await Promise.all([
          parseStoreOptions(),
          getIntercompanyModes(),
          getIntercompanyStatements(),
        ]);
        setStores(storeList);
        const preferredPerth = (storeList || []).find((s) => (s.store_id || s.store_key) === 'store_perth');
        const firstStore = preferredPerth?.store_id || preferredPerth?.store_key || storeList[0]?.store_id || storeList[0]?.store_key || '';
        const nextStore = store || firstStore;
        const nextModes = modesRes.data || [];
        setStore(nextStore);
        setModesCache(nextModes);
        setStatements(statementsRes.data || []);

        const nextMode = resolveModeForMonth(nextModes, nextStore, month);
        setMode(nextMode);
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Failed to load intercompany data');
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!store) return;
    const syncForMonth = async () => {
      setMode(resolveModeForMonth(modesCache, store, month));
    };
    syncForMonth();
  }, [store, month, modesCache]);

  const refreshStatements = async () => {
    const res = await getIntercompanyStatements();
    setStatements(res.data || []);
  };

  const handleSaveMode = async () => {
    if (!store) return;
    try {
      await upsertIntercompanyMode({ store, month, mode, notes: `Manual mode selection for ${month}` });
      const modesRes = await getIntercompanyModes();
      const nextModes = modesRes.data || [];
      setModesCache(nextModes);
      setMode(resolveModeForMonth(nextModes, store, month));
      toast.success('Month mode saved');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save mode');
    }
  };

  const loadBulkGrid = async () => {
    if (!store) return;
    try {
      const res = await getIntercompanyMonthlyRateGrid({
        store,
        start_month: reconStartMonth,
        end_month: reconEndMonth,
      });
      const rows = res.data?.rows || [];
      setBulkRows(rows);
      setBulkDirtyMonths({});
      const selected = {};
      monthRange(reconStartMonth, reconEndMonth).forEach((m) => {
        selected[m] = false;
      });
      setBulkSelection(selected);
      const firstCopyMonth = (rows.find((r) => !r.is_finalized_month) || rows[0] || {}).month || '';
      setCopyFromMonth(firstCopyMonth);

    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load monthly grid');
    }
  };

  const markBulkDirty = (month) => {
    setBulkDirtyMonths((prev) => ({ ...prev, [month]: true }));
  };

  const handleBulkCellChange = (month, fieldPath, value) => {
    const numeric = Number(value || 0);
    setBulkRows((prev) => prev.map((row) => (row.month === month ? setNested(row, fieldPath, numeric) : row)));
    markBulkDirty(month);
  };

  const toggleBulkSelect = (month, checked) => {
    setBulkSelection((prev) => ({ ...prev, [month]: checked }));
  };

  const toggleBulkSelectAll = (checked) => {
    const next = {};
    (bulkRows || []).forEach((row) => {
      next[row.month] = checked;
    });
    setBulkSelection(next);
  };

  const handleApplyBulkField = () => {
    const numeric = Number(bulkValue || 0);
    const selectedMonths = Object.keys(bulkSelection).filter((m) => bulkSelection[m]);
    if (selectedMonths.length === 0) {
      toast.error('Select at least one month');
      return;
    }
    setBulkRows((prev) => prev.map((row) => (
      selectedMonths.includes(row.month) && !row.is_finalized_month
        ? setNested(row, bulkField, numeric)
        : row
    )));
    setBulkDirtyMonths((prev) => {
      const next = { ...prev };
      selectedMonths.forEach((m) => {
        const row = (bulkRows || []).find((r) => r.month === m);
        if (row && !row.is_finalized_month) next[m] = true;
      });
      return next;
    });
    toast.success(`Applied to ${selectedMonths.length} month(s)`);
  };

  const handleSaveBulkGrid = async () => {
    if (!store) return;
    const dirtyRows = (bulkRows || []).filter((row) => bulkDirtyMonths[row.month]);
    if (dirtyRows.length === 0) {
      toast.error('No changed months to save');
      return;
    }
    setBulkSaving(true);
    try {
      const payloadRows = dirtyRows.map((row) => ({
        month: row.month,
        franchise_percent: Number(row.franchise_percent || 0),
        fabric_percent_basic: Number(row.fabric_percent_basic || 0),
        garment_rates: row.garment_rates || {},
        muslin_rates: row.muslin_rates || {},
        baste_rates: row.baste_rates || {},
        construction_rates: row.construction_rates || {},
        logistics_rates: row.logistics_rates || {},
        accessory_rates: row.accessory_rates || {},
        alteration_bulk_amount: Number(row.alteration_bulk_amount || 0),
        notes: row.notes || '',
      }));
      const res = await upsertIntercompanyMonthlyRateGrid({ store, rows: payloadRows });
      const skippedCount = res.data?.skipped_locked?.length || 0;
      const upsertedCount = res.data?.upserted_months?.length || 0;
      await loadBulkGrid();
      if (skippedCount > 0 && upsertedCount === 0) {
        toast.error('All selected months are finalized. Move statements to draft to edit rates.');
      } else if (skippedCount > 0) {
        toast.success(`Saved ${upsertedCount} month(s), skipped ${skippedCount} finalized month(s)`);
      } else {
        toast.success(`Saved ${upsertedCount} month(s)`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save monthly rates');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleCopyRowToSelectedMonths = () => {
    if (!copyFromMonth) {
      toast.error('Select a source month to copy from');
      return;
    }

    const sourceRow = (bulkRows || []).find((row) => row.month === copyFromMonth);
    if (!sourceRow) {
      toast.error('Source month row not found');
      return;
    }

    const targetMonths = Object.keys(bulkSelection).filter((monthKey) => bulkSelection[monthKey] && monthKey !== copyFromMonth);
    if (targetMonths.length === 0) {
      toast.error('Select at least one target month');
      return;
    }

    const targetMonthSet = new Set(targetMonths);
    const copiedMonths = [];
    const skippedLockedMonths = [];

    setBulkRows((prev) => prev.map((row) => {
      if (!targetMonthSet.has(row.month)) return row;
      if (row.is_finalized_month) {
        skippedLockedMonths.push(row.month);
        return row;
      }

      copiedMonths.push(row.month);
      return {
        ...row,
        franchise_percent: Number(sourceRow.franchise_percent || 0),
        fabric_percent_basic: Number(sourceRow.fabric_percent_basic || 0),
        garment_rates: { ...(sourceRow.garment_rates || {}) },
        muslin_rates: { ...(sourceRow.muslin_rates || {}) },
        baste_rates: { ...(sourceRow.baste_rates || {}) },
        construction_rates: { ...(sourceRow.construction_rates || {}) },
        logistics_rates: { ...(sourceRow.logistics_rates || {}) },
        accessory_rates: { ...(sourceRow.accessory_rates || {}) },
        alteration_bulk_amount: Number(sourceRow.alteration_bulk_amount || 0),
        notes: sourceRow.notes || '',
      };
    }));

    setBulkDirtyMonths((prev) => {
      const next = { ...prev };
      copiedMonths.forEach((monthKey) => {
        next[monthKey] = true;
      });
      return next;
    });

    if (copiedMonths.length === 0) {
      toast.error('No editable target months selected');
      return;
    }

    const skipNote = skippedLockedMonths.length ? `, skipped ${skippedLockedMonths.length} finalized` : '';
    toast.success(`Copied ${copyFromMonth} rates to ${copiedMonths.length} month(s)${skipNote}`);
  };

  const handleGenerateStatement = async () => {
    if (!store) return;
    setSaving(true);
    try {
      await generateIntercompanyStatement({
        store,
        month,
        mode,
        include_cold_data: includeCold,
      });
      await refreshStatements();
      toast.success('Statement generated');
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error(error?.response?.data?.detail || 'Finalized statement exists. Change it to draft first.');
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to generate statement');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async (statementId) => {
    try {
      await updateIntercompanyStatementStatus(statementId, 'finalized');
      await refreshStatements();
      toast.success('Statement finalized');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to finalize statement');
    }
  };

  const handleSetDraft = async (statementId) => {
    try {
      await updateIntercompanyStatementStatus(statementId, 'draft');
      await refreshStatements();
      toast.success('Statement moved to draft');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update statement status');
    }
  };

  const visibleStatements = statements.filter((s) => (!store || s.store === store) && (!month || s.month === month));

  const handleRunReconciliation = async () => {
    if (!store) return;
    setReconLoading(true);
    try {
      const res = await getIntercompanyReconciliation({
        store,
        start_month: reconStartMonth,
        end_month: reconEndMonth,
      });
      setReconciliation(res.data || null);
      toast.success('Reconciliation generated');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to run reconciliation');
    } finally {
      setReconLoading(false);
    }
  };

  const handleDownloadInvoicePdf = async (statementId) => {
    try {
      await downloadIntercompanyStatementInvoicePdf(statementId);
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      toast.error(error?.message || 'Failed to download invoice PDF');
    }
  };

  const handleDownloadInvoiceFromReconRow = async (row) => {
    const statementId = row?.statement_id;
    if (!statementId) {
      toast.error(`No statement available for ${row?.month || 'this month'}`);
      return;
    }
    await handleDownloadInvoicePdf(statementId);
  };

  const handleDownloadAllReconInvoices = async () => {
    const rows = reconciliation?.rows || [];
    const withStatements = rows.filter((r) => Boolean(r.statement_id));
    if (withStatements.length === 0) {
      toast.error('No statement invoices available in this range');
      return;
    }

    let success = 0;
    for (let i = 0; i < withStatements.length; i += 1) {
      const row = withStatements[i];
      try {
        await downloadIntercompanyStatementInvoicePdf(row.statement_id);
        success += 1;
        await new Promise((resolve) => setTimeout(resolve, 350));
      } catch (error) {
        // keep going for remaining months
      }
    }

    toast.success(`Downloaded ${success}/${withStatements.length} invoice(s)`);
  };

  const handleAutoGenerateDrafts = async () => {
    if (!store) return;
    setReconLoading(true);
    try {
      const res = await autoGenerateIntercompanyDraftsForReconciliation({
        store,
        start_month: reconStartMonth,
        end_month: reconEndMonth,
        include_cold_data: true,
      });
      await refreshStatements();
      toast.success(`Drafts generated for ${res.data?.generated_months?.length || 0} month(s)`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to auto-generate draft statements');
    } finally {
      setReconLoading(false);
    }
  };

  const loadRemittanceRows = async () => {
    if (!store) return;
    setRemittanceLoading(true);
    try {
      const res = await getIntercompanyRemittances({
        store,
        start_month: reconStartMonth,
        end_month: reconEndMonth,
      });
      setRemittanceRows(res.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load remittance entries');
    } finally {
      setRemittanceLoading(false);
    }
  };

  const handleAddRemittanceRow = () => {
    setRemittanceRows((prev) => [...prev, defaultRemittanceRow(month)]);
  };

  const handleRemittanceRowChange = (entryId, field, value) => {
    setRemittanceRows((prev) => prev.map((row) => (
      row.entry_id === entryId ? { ...row, [field]: value } : row
    )));
  };

  const handleSaveRemittanceRows = async () => {
    if (!store) return;
    setRemittanceSaving(true);
    try {
      const payloadRows = (remittanceRows || []).map((row) => ({
        entry_id: String(row.entry_id || '').startsWith('temp-') ? null : row.entry_id,
        month: row.month,
        remittance_date: row.remittance_date,
        reference: row.reference,
        amount_aud: Number(row.amount_aud || 0),
        amount_sgd: Number(row.amount_sgd || 0),
        fee_aud: Number(row.fee_aud || 0),
        fee_sgd: Number(row.fee_sgd || 0),
        source: row.source || 'manual',
        notes: row.notes || '',
        attachment_url: row.attachment_url || '',
        attachment_name: row.attachment_name || '',
        attachment_r2_key: row.attachment_r2_key || '',
        parse_confidence: Number(row.parse_confidence || 0),
        raw_line: row.raw_line || '',
      }));

      const res = await upsertIntercompanyRemittances({ store, rows: payloadRows });
      const skipped = res.data?.skipped_locked?.length || 0;
      await loadRemittanceRows();
      await handleRunReconciliation();
      toast.success(`Remittance entries saved${skipped ? `, skipped ${skipped} finalized month(s)` : ''}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save remittance entries');
    } finally {
      setRemittanceSaving(false);
    }
  };

  const handleDeleteRemittanceRow = async (entryId) => {
    if (String(entryId || '').startsWith('temp-')) {
      setRemittanceRows((prev) => prev.filter((row) => row.entry_id !== entryId));
      return;
    }
    try {
      await deleteIntercompanyRemittance(entryId);
      await loadRemittanceRows();
      await handleRunReconciliation();
      toast.success('Remittance entry deleted');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete remittance entry');
    }
  };

  const handleUploadRemittanceAttachmentForRow = async (entryId, file) => {
    if (!store || !file) return;
    const targetRow = (remittanceRows || []).find((row) => row.entry_id === entryId);
    const targetMonth = targetRow?.month || month;
    if (!targetMonth) {
      toast.error('Select month before uploading attachment');
      return;
    }
    try {
      const res = await uploadIntercompanyRemittanceAttachment({
        store,
        month: targetMonth,
        file,
      });
      setRemittanceRows((prev) => prev.map((row) => (
        row.entry_id === entryId
          ? {
            ...row,
            attachment_url: res.data?.attachment_url || '',
            attachment_name: res.data?.attachment_name || file.name,
            attachment_r2_key: res.data?.attachment_r2_key || '',
          }
          : row
      )));
      toast.success('Attachment uploaded');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to upload attachment');
    }
  };

  const handleParseRemittancePdf = async () => {
    if (!store || !remittancePdfFile) {
      toast.error('Select a PDF file first');
      return;
    }
    setParsingRemittancePdf(true);
    try {
      const res = await parseIntercompanyRemittancePdf({ store, file: remittancePdfFile });
      setParsedRemittanceRows(res.data?.parsed_rows || []);
      toast.success(`Parsed ${res.data?.parsed_rows?.length || 0} row(s) from PDF`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to parse PDF');
    } finally {
      setParsingRemittancePdf(false);
    }
  };

  const handleApplyParsedRowsToGrid = () => {
    if ((parsedRemittanceRows || []).length === 0) {
      toast.error('No parsed rows available to add');
      return;
    }
    const mapped = parsedRemittanceRows.map((row) => ({
      ...defaultRemittanceRow(row.month || month),
      month: row.month || month,
      remittance_date: row.remittance_date || '',
      reference: row.reference || '',
      amount_aud: Number(row.amount_aud || 0),
      amount_sgd: Number(row.amount_sgd || 0),
      fee_aud: Number(row.fee_aud || 0),
      fee_sgd: Number(row.fee_sgd || 0),
      source: row.source || 'wise-pdf-parse',
      notes: row.notes || '',
      attachment_url: row.attachment_url || '',
      attachment_name: row.attachment_name || '',
      attachment_r2_key: row.attachment_r2_key || '',
      parse_confidence: Number(row.parse_confidence || 0),
      raw_line: row.raw_line || '',
    }));
    setRemittanceRows((prev) => [...prev, ...mapped]);
    toast.success(`Added ${mapped.length} parsed row(s) to remittance grid`);
  };

  const handleDownloadRemittanceAttachment = async (row) => {
    if (!row?.entry_id || String(row.entry_id).startsWith('temp-')) {
      toast.error('Save the row first to download attachment');
      return;
    }
    try {
      await downloadIntercompanyRemittanceAttachment(row.entry_id, row.attachment_name || 'remittance-attachment.pdf');
      toast.success('Attachment downloaded');
    } catch (error) {
      toast.error(error?.message || 'Failed to download attachment');
    }
  };

  return (
    <div className="space-y-6 p-3" data-testid="intercompany-costing-page">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label data-testid="intercompany-store-label">Store</Label>
          <Select
            value={store}
            onValueChange={(nextStore) => {
              setStore(nextStore);
            }}
          >
            <SelectTrigger className="w-[220px]" data-testid="intercompany-store-select">
              <SelectValue placeholder="Select store" />
            </SelectTrigger>
            <SelectContent>
              {stores.map((s) => (
                <SelectItem
                  key={s.store_id || s.store_key}
                  value={s.store_id || s.store_key}
                >
                  {s.name || s.store_name || s.store_id || s.store_key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label data-testid="intercompany-month-label">Month</Label>
          <Input
            type="month"
            value={month}
            onChange={(e) => {
              const nextMonth = e.target.value;
              setMonth(nextMonth);
            }}
            data-testid="intercompany-month-input"
          />
        </div>

        <div className="space-y-1.5">
          <Label data-testid="intercompany-mode-label">Pricing Mode</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="w-[180px]" data-testid="intercompany-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="basic">Basic (Legacy)</SelectItem>
              <SelectItem value="detailed">Detailed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={handleSaveMode} data-testid="intercompany-save-mode-button">Save Mode</Button>
      </div>

      <Tabs defaultValue="bulk-rates" className="space-y-4" data-testid="intercompany-tabs">
        <TabsList>
          <TabsTrigger value="bulk-rates" data-testid="intercompany-tab-bulk-rates">Bulk Rates</TabsTrigger>
          <TabsTrigger value="statements" data-testid="intercompany-tab-statements">Statements</TabsTrigger>
          <TabsTrigger value="remittances" data-testid="intercompany-tab-remittances">Remittances</TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="intercompany-tab-reconciliation">Reconciliation</TabsTrigger>
        </TabsList>

        <TabsContent value="bulk-rates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle data-testid="intercompany-bulk-title">Bulk Month-wise Rate Editor</CardTitle>
              <CardDescription data-testid="intercompany-bulk-description">
                Edit fees/rates across multiple months and save in one action.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label data-testid="intercompany-bulk-start-label">Start Month</Label>
                  <Input type="month" value={reconStartMonth} onChange={(e) => setReconStartMonth(e.target.value)} data-testid="intercompany-bulk-start-month" />
                </div>
                <div className="space-y-1.5">
                  <Label data-testid="intercompany-bulk-end-label">End Month</Label>
                  <Input type="month" value={reconEndMonth} onChange={(e) => setReconEndMonth(e.target.value)} data-testid="intercompany-bulk-end-month" />
                </div>
                <Button variant="outline" onClick={loadBulkGrid} data-testid="intercompany-bulk-load-button">Load Month Grid</Button>
              </div>

              <div className="rounded-lg border p-3 bg-secondary/20 space-y-3" data-testid="intercompany-bulk-actions-panel">
                <div className="grid md:grid-cols-5 gap-3 items-end">
                  <div className="space-y-1.5">
                    <Label data-testid="intercompany-bulk-copy-from-label">Copy From Month</Label>
                    <Select value={copyFromMonth} onValueChange={setCopyFromMonth}>
                      <SelectTrigger data-testid="intercompany-bulk-copy-from-month-select">
                        <SelectValue placeholder="Select month" />
                      </SelectTrigger>
                      <SelectContent>
                        {(bulkRows || []).map((row) => (
                          <SelectItem key={`copy-from-${row.month}`} value={row.month}>{row.month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Button variant="outline" onClick={handleCopyRowToSelectedMonths} data-testid="intercompany-bulk-copy-row-button">
                      Copy Full Row to Selected
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    <Label data-testid="intercompany-bulk-field-label">Field</Label>
                    <Select value={bulkField} onValueChange={setBulkField}>
                      <SelectTrigger data-testid="intercompany-bulk-field-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {bulkFieldOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label data-testid="intercompany-bulk-value-label">Value</Label>
                    <Input type="number" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} data-testid="intercompany-bulk-value-input" />
                  </div>
                  <div>
                    <Button variant="outline" onClick={handleApplyBulkField} data-testid="intercompany-bulk-apply-button">Apply to Selected Months</Button>
                  </div>
                  <div className="text-sm text-muted-foreground" data-testid="intercompany-bulk-dirty-count">
                    Dirty months: {Object.keys(bulkDirtyMonths).length}
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveBulkGrid} disabled={bulkSaving} data-testid="intercompany-bulk-save-all-button">
                    {bulkSaving ? 'Saving...' : 'Save All Changed Months'}
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border overflow-x-auto" data-testid="intercompany-bulk-grid-table">
                <table className="min-w-[2100px] w-full text-sm">
                  <thead>
                    <tr className="bg-secondary/40 text-left">
                      <th className="p-2 border-b">
                        <Switch
                          checked={(bulkRows || []).length > 0 && (bulkRows || []).every((r) => bulkSelection[r.month])}
                          onCheckedChange={(checked) => toggleBulkSelectAll(checked)}
                          data-testid="intercompany-bulk-select-all-switch"
                        />
                      </th>
                      <th className="p-2 border-b">Month</th>
                      <th className="p-2 border-b">Status</th>
                      {editableBulkColumns.map((col) => <th key={col.key} className="p-2 border-b">{col.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {(bulkRows || []).map((row) => {
                      const isLocked = Boolean(row.is_finalized_month);
                      return (
                        <tr key={`bulk-row-${row.month}`} data-testid={`intercompany-bulk-row-${row.month}`}>
                          <td className="p-2 border-b">
                            <Switch
                              checked={Boolean(bulkSelection[row.month])}
                              onCheckedChange={(checked) => toggleBulkSelect(row.month, checked)}
                              data-testid={`intercompany-bulk-select-${row.month}`}
                            />
                          </td>
                          <td className="p-2 border-b" data-testid={`intercompany-bulk-month-${row.month}`}>{row.month}</td>
                          <td className="p-2 border-b" data-testid={`intercompany-bulk-status-${row.month}`}>{isLocked ? 'Finalized (Locked)' : 'Editable'}</td>
                          {editableBulkColumns.map((col) => (
                            <td key={`${row.month}-${col.key}`} className="p-2 border-b">
                              <Input
                                type="number"
                                value={getNested(row, col.key) || 0}
                                onChange={(e) => handleBulkCellChange(row.month, col.key, e.target.value)}
                                disabled={isLocked}
                                data-testid={`intercompany-bulk-input-${row.month}-${col.key.replace(/\./g, '-')}`}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                    {(bulkRows || []).length === 0 && (
                      <tr>
                        <td colSpan={editableBulkColumns.length + 3} className="p-4 text-center text-muted-foreground" data-testid="intercompany-bulk-empty-state">
                          No months loaded. Choose range and click &quot;Load Month Grid&quot;.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statements" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle data-testid="intercompany-generate-title">Generate Monthly Statement</CardTitle>
              <CardDescription>
                Mode can be basic or detailed per month. Detailed can be activated only when ready.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch checked={includeCold} onCheckedChange={setIncludeCold} data-testid="intercompany-include-cold-switch" />
                  <Label>Include cold data</Label>
                </div>
                <Button onClick={handleGenerateStatement} disabled={saving} data-testid="intercompany-generate-statement-button">
                  {saving ? 'Generating...' : 'Generate Statement'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle data-testid="intercompany-statements-list-title">Statements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {visibleStatements.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="intercompany-statements-empty">No statements for selected store/month.</p>
              )}
              {visibleStatements.map((s) => (
                <div key={s.statement_id} className="rounded-lg border p-3" data-testid={`intercompany-statement-${s.statement_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium">{s.month} • {s.store}</p>
                      <p className="text-xs text-muted-foreground">Mode: {s.mode} • Total: SGD {Number(s.total_amount || 0).toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleDownloadInvoicePdf(s.statement_id)} data-testid={`intercompany-download-invoice-${s.statement_id}`}>
                        Invoice PDF
                      </Button>
                      <Badge variant={s.status === 'finalized' ? 'default' : 'secondary'} data-testid={`intercompany-statement-status-${s.statement_id}`}>
                        {s.status}
                      </Badge>
                      {s.status !== 'finalized' && (
                        <Button size="sm" variant="outline" onClick={() => handleFinalize(s.statement_id)} data-testid={`intercompany-finalize-${s.statement_id}`}>
                          Finalize
                        </Button>
                      )}
                      {s.status === 'finalized' && (
                        <Button size="sm" variant="outline" onClick={() => handleSetDraft(s.statement_id)} data-testid={`intercompany-set-draft-${s.statement_id}`}>
                          Set Draft
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {(s.line_items || []).map((line) => (
                      <div key={`${s.statement_id}-${line.line_code}`} className="rounded-md bg-secondary/30 p-2" data-testid={`intercompany-line-${s.statement_id}-${line.line_code}`}>
                        <p className="text-xs text-muted-foreground">{line.label}</p>
                        <p className="font-semibold">SGD {Number(line.amount || 0).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="remittances" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle data-testid="intercompany-remittances-title">Wise Remittance Details</CardTitle>
              <CardDescription data-testid="intercompany-remittances-description">
                Upload and parse Wise PDFs, then review/edit remittance rows before saving.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label data-testid="intercompany-remit-start-month-label">Start Month</Label>
                  <Input type="month" value={reconStartMonth} onChange={(e) => setReconStartMonth(e.target.value)} data-testid="intercompany-remit-start-month" />
                </div>
                <div className="space-y-1.5">
                  <Label data-testid="intercompany-remit-end-month-label">End Month</Label>
                  <Input type="month" value={reconEndMonth} onChange={(e) => setReconEndMonth(e.target.value)} data-testid="intercompany-remit-end-month" />
                </div>
                <Button variant="outline" onClick={loadRemittanceRows} disabled={remittanceLoading} data-testid="intercompany-remit-load-button">
                  {remittanceLoading ? 'Loading...' : 'Load Remittances'}
                </Button>
                <Button variant="outline" onClick={handleAddRemittanceRow} data-testid="intercompany-remit-add-row-button">
                  Add Row
                </Button>
                <Button onClick={handleSaveRemittanceRows} disabled={remittanceSaving} data-testid="intercompany-remit-save-rows-button">
                  {remittanceSaving ? 'Saving...' : 'Save Remittance Rows'}
                </Button>
                <div className="space-y-1.5">
                  <Label data-testid="intercompany-remit-sort-field-label">Sort By</Label>
                  <Select value={remittanceSortField} onValueChange={setRemittanceSortField}>
                    <SelectTrigger className="w-[180px]" data-testid="intercompany-remit-sort-field-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="remittance_date">Date</SelectItem>
                      <SelectItem value="month">Month</SelectItem>
                      <SelectItem value="amount_sgd">Amount SGD</SelectItem>
                      <SelectItem value="amount_aud">Amount AUD</SelectItem>
                      <SelectItem value="reference">Reference</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setRemittanceSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                  data-testid="intercompany-remit-sort-direction-button"
                >
                  {remittanceSortDirection === 'asc' ? 'Ascending ↑' : 'Descending ↓'}
                </Button>
              </div>

              <div className="rounded-lg border p-3 bg-secondary/20 space-y-3" data-testid="intercompany-remit-parse-panel">
                <div className="grid md:grid-cols-4 gap-3 items-end">
                  <div className="space-y-1.5 md:col-span-2">
                    <Label data-testid="intercompany-remit-parse-file-label">Wise PDF</Label>
                    <Input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => setRemittancePdfFile(e.target.files?.[0] || null)}
                      data-testid="intercompany-remit-parse-file-input"
                    />
                  </div>
                  <div>
                    <Button variant="outline" onClick={handleParseRemittancePdf} disabled={parsingRemittancePdf} data-testid="intercompany-remit-parse-button">
                      {parsingRemittancePdf ? 'Parsing...' : 'Parse PDF'}
                    </Button>
                  </div>
                  <div>
                    <Button variant="outline" onClick={handleApplyParsedRowsToGrid} data-testid="intercompany-remit-apply-parsed-button">
                      Add Parsed Rows to Grid
                    </Button>
                  </div>
                </div>

                {(parsedRemittanceRows || []).length > 0 && (
                  <div className="rounded border overflow-x-auto" data-testid="intercompany-remit-parsed-preview-table">
                    <table className="min-w-[980px] w-full text-sm">
                      <thead>
                        <tr className="bg-secondary/30">
                          <th className="p-2 border-b">Month</th>
                          <th className="p-2 border-b">Date</th>
                          <th className="p-2 border-b">Reference</th>
                          <th className="p-2 border-b text-right">AUD</th>
                          <th className="p-2 border-b text-right">SGD</th>
                          <th className="p-2 border-b text-right">Confidence</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(parsedRemittanceRows || []).map((row, idx) => (
                          <tr key={`parsed-remit-${idx}`} data-testid={`intercompany-remit-parsed-row-${idx}`}>
                            <td className="p-2 border-b">{row.month || '—'}</td>
                            <td className="p-2 border-b">{row.remittance_date || '—'}</td>
                            <td className="p-2 border-b truncate">{row.reference || '—'}</td>
                            <td className="p-2 border-b text-right">{Number(row.amount_aud || 0).toFixed(2)}</td>
                            <td className="p-2 border-b text-right">{Number(row.amount_sgd || 0).toFixed(2)}</td>
                            <td className="p-2 border-b text-right">{Number(row.parse_confidence || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="rounded-lg border overflow-x-auto shadow-sm" data-testid="intercompany-remit-grid-table">
                <table className="min-w-[1650px] w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-left">
                      <th className="p-2 border-b">Month</th>
                      <th className="p-2 border-b">Date</th>
                      <th className="p-2 border-b">Reference</th>
                      <th className="p-2 border-b text-right">AUD</th>
                      <th className="p-2 border-b text-right">SGD</th>
                      <th className="p-2 border-b text-right">Fee AUD</th>
                      <th className="p-2 border-b text-right">Fee SGD</th>
                      <th className="p-2 border-b">Source</th>
                      <th className="p-2 border-b">Attachment</th>
                      <th className="p-2 border-b">Notes</th>
                      <th className="p-2 border-b">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sortedRemittanceRows || []).map((row, idx) => (
                      <tr key={`remit-row-${row.entry_id}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} data-testid={`intercompany-remit-row-${row.entry_id}`}>
                        <td className="p-2 border-b">
                          <Input
                            type="month"
                            value={row.month || ''}
                            onChange={(e) => handleRemittanceRowChange(row.entry_id, 'month', e.target.value)}
                            data-testid={`intercompany-remit-month-${row.entry_id}`}
                          />
                        </td>
                        <td className="p-2 border-b">
                          <Input
                            type="date"
                            value={row.remittance_date || ''}
                            onChange={(e) => handleRemittanceRowChange(row.entry_id, 'remittance_date', e.target.value)}
                            data-testid={`intercompany-remit-date-${row.entry_id}`}
                          />
                        </td>
                        <td className="p-2 border-b">
                          <Input
                            value={row.reference || ''}
                            onChange={(e) => handleRemittanceRowChange(row.entry_id, 'reference', e.target.value)}
                            data-testid={`intercompany-remit-ref-${row.entry_id}`}
                          />
                        </td>
                        <td className="p-2 border-b">
                          <Input
                            type="number"
                            value={row.amount_aud || 0}
                            onChange={(e) => handleRemittanceRowChange(row.entry_id, 'amount_aud', Number(e.target.value || 0))}
                            data-testid={`intercompany-remit-aud-${row.entry_id}`}
                          />
                        </td>
                        <td className="p-2 border-b">
                          <Input
                            type="number"
                            value={row.amount_sgd || 0}
                            onChange={(e) => handleRemittanceRowChange(row.entry_id, 'amount_sgd', Number(e.target.value || 0))}
                            data-testid={`intercompany-remit-sgd-${row.entry_id}`}
                          />
                        </td>
                        <td className="p-2 border-b">
                          <Input
                            type="number"
                            value={row.fee_aud || 0}
                            onChange={(e) => handleRemittanceRowChange(row.entry_id, 'fee_aud', Number(e.target.value || 0))}
                            data-testid={`intercompany-remit-fee-aud-${row.entry_id}`}
                          />
                        </td>
                        <td className="p-2 border-b">
                          <Input
                            type="number"
                            value={row.fee_sgd || 0}
                            onChange={(e) => handleRemittanceRowChange(row.entry_id, 'fee_sgd', Number(e.target.value || 0))}
                            data-testid={`intercompany-remit-fee-sgd-${row.entry_id}`}
                          />
                        </td>
                        <td className="p-2 border-b">
                          <Input
                            value={row.source || ''}
                            onChange={(e) => handleRemittanceRowChange(row.entry_id, 'source', e.target.value)}
                            data-testid={`intercompany-remit-source-${row.entry_id}`}
                          />
                        </td>
                        <td className="p-2 border-b space-y-2">
                          <Input
                            type="file"
                            accept="application/pdf,image/jpeg,image/png,image/webp"
                            onChange={(e) => handleUploadRemittanceAttachmentForRow(row.entry_id, e.target.files?.[0] || null)}
                            data-testid={`intercompany-remit-upload-${row.entry_id}`}
                          />
                          <div className="flex items-center gap-2">
                            <span className="text-xs truncate" data-testid={`intercompany-remit-attachment-name-${row.entry_id}`}>{row.attachment_name || 'No attachment'}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadRemittanceAttachment(row)}
                              disabled={!row.attachment_name}
                              data-testid={`intercompany-remit-download-attachment-${row.entry_id}`}
                            >
                              Download
                            </Button>
                          </div>
                        </td>
                        <td className="p-2 border-b">
                          <Input
                            value={row.notes || ''}
                            onChange={(e) => handleRemittanceRowChange(row.entry_id, 'notes', e.target.value)}
                            data-testid={`intercompany-remit-notes-${row.entry_id}`}
                          />
                        </td>
                        <td className="p-2 border-b">
                          <Button size="sm" variant="outline" onClick={() => handleDeleteRemittanceRow(row.entry_id)} data-testid={`intercompany-remit-delete-${row.entry_id}`}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {(sortedRemittanceRows || []).length === 0 && (
                      <tr>
                        <td colSpan={11} className="p-4 text-center text-muted-foreground" data-testid="intercompany-remit-empty-state">
                          No remittance rows loaded. Click Load Remittances or Add Row.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reconciliation" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle data-testid="intercompany-reconciliation-title">Wise vs Statement Reconciliation</CardTitle>
              <CardDescription>
                Compare month-wise statement totals against Wise remittances in SGD.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1.5">
                  <Label>Start Month</Label>
                  <Input type="month" value={reconStartMonth} onChange={(e) => setReconStartMonth(e.target.value)} data-testid="intercompany-recon-start-month" />
                </div>
                <div className="space-y-1.5">
                  <Label>End Month</Label>
                  <Input type="month" value={reconEndMonth} onChange={(e) => setReconEndMonth(e.target.value)} data-testid="intercompany-recon-end-month" />
                </div>
                <Button variant="outline" onClick={handleAutoGenerateDrafts} disabled={reconLoading} data-testid="intercompany-auto-generate-drafts-button">
                  Auto-Generate Drafts (Skips Finalized)
                </Button>
                <Button onClick={handleRunReconciliation} disabled={reconLoading} data-testid="intercompany-run-reconciliation-button">
                  {reconLoading ? 'Running...' : 'Run Reconciliation'}
                </Button>
                <Button variant="outline" onClick={handleDownloadAllReconInvoices} data-testid="intercompany-download-all-recon-invoices-button">
                  Download All Invoices
                </Button>
              </div>

              <div className="rounded-lg border p-3 bg-secondary/20" data-testid="intercompany-reconciliation-source-note">
                <p className="text-sm text-muted-foreground">
                  Reconciliation now uses remittance rows maintained in the <strong>Remittances</strong> tab as the single source.
                </p>
              </div>

              {reconciliation && (
                <div className="space-y-3" data-testid="intercompany-reconciliation-results">
                  <div className="grid md:grid-cols-3 gap-3">
                    <div className="rounded-lg border p-3 bg-secondary/30">
                      <p className="text-xs text-muted-foreground">Statement Total (SGD)</p>
                      <p className="text-xl font-semibold">{Number(reconciliation?.totals?.statement_total_sgd || 0).toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg border p-3 bg-secondary/30">
                      <p className="text-xs text-muted-foreground">Wise Remittance Total (SGD)</p>
                      <p className="text-xl font-semibold">{Number(reconciliation?.totals?.wise_remittance_total_sgd || 0).toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg border p-3 bg-secondary/30">
                      <p className="text-xs text-muted-foreground">Variance (SGD)</p>
                      <p className={`text-xl font-semibold ${Number(reconciliation?.totals?.variance_sgd || 0) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                        {Number(reconciliation?.totals?.variance_sgd || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-lg border overflow-hidden" data-testid="intercompany-reconciliation-table">
                    <div className="grid grid-cols-9 gap-2 px-3 py-2 text-xs font-semibold bg-secondary/40">
                      <div>Month</div>
                      <div>Status</div>
                      <div>Mode</div>
                      <div className="text-right">Statement (SGD)</div>
                      <div className="text-right">Wise (SGD)</div>
                      <div className="text-right">Remit FX</div>
                      <div className="text-right">Variance</div>
                      <div>Invoice</div>
                      <div>ID</div>
                    </div>
                    {(reconciliation?.rows || []).map((row) => (
                      <div key={`recon-${row.month}`} className="grid grid-cols-9 gap-2 px-3 py-2 text-sm border-t" data-testid={`intercompany-recon-row-${row.month}`}>
                        <div>{row.month}</div>
                        <div>{row.statement_status || 'missing'}</div>
                        <div>{row.statement_mode || '—'}</div>
                        <div className="text-right">{Number(row.statement_total_sgd || 0).toFixed(2)}</div>
                        <div className="text-right">{Number(row.wise_remittance_total_sgd || 0).toFixed(2)}</div>
                        <div className="text-right">{Number(row.aud_to_sgd_rate || 0).toFixed(4)}</div>
                        <div className={`text-right ${Number(row.variance_sgd || 0) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                          {Number(row.variance_sgd || 0).toFixed(2)}
                        </div>
                        <div>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!row.statement_id}
                            onClick={() => handleDownloadInvoiceFromReconRow(row)}
                            data-testid={`intercompany-recon-download-invoice-${row.month}`}
                          >
                            Invoice
                          </Button>
                        </div>
                        <div className="truncate">{row.statement_id || '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
